import type { SimpleExperiment } from "@v1/features/experiments/tabs/hooks/useExperimentDetails";
import type {
  AnalyticsQuery,
  PageType,
  RangeResult,
} from "schemas/generated/analyticsRead";
import type { Experiment } from "schemas/generated/experiment";
import type { CurrencyCode } from "schemas/project";

import * as React from "react";

import { TZDate } from "@date-fns/tz";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@v1/components/designSystem/shadcn/core/table";
import { OVERVIEW_TABLE_METRICS } from "@v1/features/analytics/constants";
import MetricWithDelta from "@v1/features/analytics/MetricWithDelta";
import useBasicAnalyticsRead from "@v1/features/analytics/useBasicAnalyticsRead";
import { ExperimentActionsBar } from "@v1/features/experiments/components/ExperimentActionsBar";
import { experimentStatusMapping } from "@v1/features/experiments/experimentStatus";
import { ExperimentsLayout } from "@v1/features/experiments/Layout";
import { ExperimentTabWrapper } from "@v1/features/experiments/tabs/ExperimentTabWrapper";
import { useExperimentDetails } from "@v1/features/experiments/tabs/hooks/useExperimentDetails";
import { Loader } from "@v2/components/Loader";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceContext } from "@v2/contexts/WorkspaceDashboardContext";
import {
  DEFAULT_FILTERS,
  DEFAULT_QUERY,
} from "@v2/features/analytics/constants";
import { useSubscriptionInfo } from "@v2/hooks/subscription";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { docs } from "@v2/utils/docs";
import { routes } from "@v2/utils/router";
import { adjustEndTimeToFullDays } from "analytics-utils/analytics";
import copy from "copy-to-clipboard";
import { endOfDay, startOfDay } from "date-fns";
import { ArrowUpRight, Copy, ExternalLink, Minus, Network } from "lucide-react";
import { generatePath, Link, useNavigate } from "react-router-dom";
import { convertMsToDays } from "replo-utils/lib/datetime";
import { formatWithCommasAndTwoDecimals } from "replo-utils/lib/math";
import { DEFAULT_MAIN_RANGE_ID } from "schemas/analyticsRead";
import { getExperimentStatus } from "schemas/experiment";

import { VariationNameStartEnhancer } from "../components/VariationNameStartEnhancer";
import { ExperimentEditableHeader } from "../ExperimentEditableHeader";
import { useExperimentApi } from "../utils";
import { useExperimentEdit } from "./hooks/useExperimentEdit";

type ValueContainerProps = React.PropsWithChildren<{
  className?: string;
}>;

const ValuesContainer: React.FC<ValueContainerProps> = ({
  className,
  children,
}) => {
  return (
    <div
      className={twMerge(
        "border-0.5 border-border col-span-6 flex flex-row gap-8 rounded-md p-4",
        className,
      )}
    >
      {children}
    </div>
  );
};

type TitleWithValueProps = {
  title: string;
  value: string | null;
  valueAsChildren?: React.ReactNode;
};

const TitleWithValue: React.FC<TitleWithValueProps> = ({ title, value }) => {
  return (
    <div className="text-default flex flex-col gap-2">
      <span className="text-xs font-medium">{title}</span>
      <span className="text-xs">{value !== null ? value : <Minus />}</span>
    </div>
  );
};

type PageTypeWithDescriptionProps = {
  title: string;
  description: string;
  isSelected: boolean;
  className?: string;
};

const PageTypeWithDescription: React.FC<PageTypeWithDescriptionProps> = ({
  title,
  description,
  isSelected,
  className,
}) => {
  return (
    <div
      className={twMerge(
        className,
        "flex w-full flex-col rounded-sm p-1.5",
        isSelected && "bg-selectable-selected",
        !isSelected && "hover:bg-selectable-hover",
      )}
    >
      <p className="text-xs font-semibold text-default">{title}</p>
      <p className="text-muted text-xs">{description}</p>
    </div>
  );
};

type ExperimentPageTypeSelectProps = {
  pageType: PageType;
  isFractional: boolean;
  onChange: (pageType: PageType, isFractional: boolean) => void;
};

const ExperimentPageTypeSelect: React.FC<ExperimentPageTypeSelectProps> = ({
  pageType,
  isFractional,
  onChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOpenChange = () => {
    setIsOpen(!isOpen);
  };

  // Create a combined value to represent the current selection
  const currentValue = isFractional ? "fractional" : pageType;

  const options = [
    {
      displayValue: "All Viewed Pages",
      label: (
        <PageTypeWithDescription
          title="All Viewed Pages"
          description="Data for any page visited within the session"
          isSelected={pageType === "allPages" && !isFractional}
        />
      ),
      value: "allPages",
      isDisabled: false,
    },
    {
      displayValue: "Entry Pages",
      label: (
        <PageTypeWithDescription
          title="Entry Pages"
          description="Shows metrics for pages that began the session"
          isSelected={pageType === "entryPages" && !isFractional}
        />
      ),
      value: "entryPages",
      isDisabled: false,
    },
    {
      displayValue: "Fractional Attribution",
      label: (
        <PageTypeWithDescription
          title="Fractional Attribution"
          description="Uses fractional attribution model for conversion tracking"
          isSelected={isFractional}
        />
      ),
      value: "fractional" as const,
      isDisabled: false,
    },
  ];

  const handleChange = (newValue: string) => {
    if (newValue === "fractional") {
      onChange("entryPages", true);
    } else {
      onChange(newValue as PageType, false);
    }
  };

  return (
    <Combobox.Root
      options={options}
      open={isOpen}
      onOpenChange={handleOpenChange}
      value={currentValue}
      onChange={handleChange}
    >
      <Combobox.Trigger>
        <Combobox.SelectionButton
          size="base"
          title={
            options.find((option) => option.value === currentValue)
              ?.displayValue ?? ""
          }
          isPlaceholder={false}
        />
      </Combobox.Trigger>
      <Combobox.Popover side="bottom" align="end" layoutClassName="w-[300px]">
        <Combobox.Content size="lg" />
      </Combobox.Popover>
    </Combobox.Root>
  );
};

/**
 * Normalizes URL paths to match how they are stored in the analytics database.
 * The backend (transformStoredEventToV2 in analytics-database/src/utils.ts) strips
 * trailing slashes from paths before storing them, so "/" becomes "".
 * This ensures our filters match the actual stored data.
 *
 * @author Ryan Voris 2025-06-26
 */
const normalizeUrlPath = (path: string): string => {
  return path.replace(/\/$/, "");
};

const useAnalyticsReadForExperiments = (
  experimentId: string,
  startDatetime: number,
  endDatetime: number,
  interval: number,
  variationPagePaths: string[],
  pageType: PageType,
  isFractional: boolean,
) => {
  /**
   * NOTE (Max, 2024-10-17): We use a basic query where we get:
   * 1) mainRange datetimes from the Experiment's createdAt / endedAt dates
   * 2) filters using the experimentId: we're only interested in CH rows that contain
   * the experimentId as a urlParam
   * 3) urlPath filters from the experiment variation urls to narrow down to specific pages
   * 4) pageType filter to determine whether to show all pages or entry pages only
   * 5) isFractional flag to determine whether to use fractional attribution
   *
   * We do not filter by urlHosts/domain because:
   * - urlPath + experimentId filters are sufficient for experiments
   * - Events may be logged under slightly different domains (www.domain.com vs domain.com)
   * - The backend will skip the domain filter when urlHosts is empty
   */
  const query: AnalyticsQuery = React.useMemo(
    () => ({
      ...DEFAULT_QUERY,
      order: "DESC",
      sortMetric: "unique_sessions",
      ranges: {
        mainRange: {
          id: DEFAULT_MAIN_RANGE_ID,
          startDatetime: startDatetime,
          endDatetime: endDatetime,
          interval,
        },
        compareAtRanges: [],
      },
      filters: {
        ...DEFAULT_FILTERS,
        pageType,
        urlPath: [
          {
            operator: "equals",
            value: variationPagePaths,
          },
        ],
        urlParams: {
          "replo-experiment-id": [
            {
              operator: "equals",
              value: [`${experimentId}`],
            },
          ],
        },
      },
      mode: "groupByUrlPath",
      isFractional,
    }),
    [
      experimentId,
      startDatetime,
      endDatetime,
      interval,
      variationPagePaths,
      pageType,
      isFractional,
    ],
  );

  return useBasicAnalyticsRead({
    type: "withoutUrlSyncQuery",
    queryConfig: query,
    isExperimentQuery: true,
  });
};

const ExperimentResultsTabLinkInfo: React.FC<{
  experiment: Experiment;
}> = ({ experiment }) => {
  const logEvent = useLogAnalytics();
  const { subscriptionInfo } = useSubscriptionInfo();
  const subscriptionTier = subscriptionInfo?.tier || "free";

  const {
    links: { data: links },
  } = useExperimentApi();

  const getSelectedLinkUrl = (): string => {
    const link = links?.find((link) => {
      return (
        link.id ===
        (experiment.completedAnalyticsLinkId ?? experiment.analyticsLinkId)
      );
    });
    return link?.url ?? "";
  };

  return (
    <div className="bg-sheet border-0.5 border-border flex flex-col sm:flex-row items-start sm:items-center justify-between rounded-md px-4 py-3 gap-3">
      <div className="flex flex-row gap-1.5">
        <Network size={16} />
        <div className="flex flex-col">
          <span className="text-default text-xs font-medium">
            Direct traffic to your Test Link
          </span>
          <span className="text-placeholder text-xs">
            Only traffic directed through this test link will be included in the
            test.
          </span>
        </div>
      </div>
      <div className="flex flex-row gap-2.5 sm:ml-auto">
        <DeprecatedButton
          variant="tertiary"
          size="base"
          endEnhancer={<ExternalLink size={14} />}
          onClick={() => {
            const url = getSelectedLinkUrl();
            if (url) {
              window.open(url, "_blank");
              logEvent("experiment.link.preview", {
                billingPlanTier: subscriptionTier,
              });
            }
          }}
        >
          <span className="text-default text-xs font-medium">Preview</span>
        </DeprecatedButton>
        <DeprecatedButton
          variant="primary"
          size="base"
          endEnhancer={<Copy size={14} />}
          onClick={() => {
            const url = getSelectedLinkUrl();
            if (url) {
              copy(url);
              successToast(
                "Link Copied",
                "The test link has been copied to your clipboard.",
              );
              logEvent("experiment.link.copy", {
                billingPlanTier: subscriptionTier,
              });
            }
          }}
        >
          <span className="text-xs font-medium text-white">Copy link</span>
        </DeprecatedButton>
      </div>
    </div>
  );
};

const StatSigInfoText: React.FC = () => {
  return (
    <div className="flex flex-row items-center justify-end gap-1">
      <span className="text-muted text-xs">
        Want to see if you've reached stat sig?
      </span>
      <DeprecatedButton
        variant="tertiary"
        size="sm"
        endEnhancer={<ArrowUpRight size={12} className="text-primary" />}
        to={docs.abTesting.education.statSigCalculator}
        target="_blank"
      >
        <span className="text-primary text-xs font-medium">
          Use our calculator
        </span>
      </DeprecatedButton>
    </div>
  );
};

const ExperimentResultsTabInner: React.FC<{
  simpleExperiment: SimpleExperiment;
  workspaceId: string | undefined;
  experiment: Experiment | null;
}> = ({ simpleExperiment, experiment, workspaceId }) => {
  const { dispatchExperimentEdit, handleReactHookFormSubmit } =
    useExperimentEdit();
  const projectId = useCurrentProjectId();
  const { workspace } = useCurrentWorkspaceContext();
  const { project } = useCurrentProjectContext();

  // NOTE (Ryan, 2026-01-06): Get currency from project/workspace for A/B testing
  // analytics display, falling back to USD if neither is set.
  const currency: CurrencyCode =
    (project?.currency as CurrencyCode) ??
    (workspace?.currency as CurrencyCode) ??
    "USD";

  const navigate = useNavigate();

  const [pageType, setPageType] = React.useState<PageType>("entryPages");
  const [isFractional, setIsFractional] = React.useState(true);

  const {
    id,
    createdAt,
    createdAtHuman,
    endedAt,
    endedAtHuman,
    variations,
    name,
  } = simpleExperiment;

  const winner = variations.find((variation) => {
    return experiment?.selectedDisplayWinnerVariationId === variation.id;
  });

  const timeZone = project?.timeZone ?? workspace?.timeZone ?? "UTC";
  const startDatetime = startOfDay(new TZDate(createdAt, timeZone)).valueOf();
  const adjustedEndDatetime = adjustEndTimeToFullDays(
    startDatetime,
    endOfDay(
      new TZDate(endedAt ? endedAt.valueOf() : Date.now(), timeZone),
    ).valueOf() + 1,
  );
  const interval = adjustedEndDatetime - startDatetime;

  const variationTargetPagePaths = simpleExperiment.variations
    .filter((variation) => variation.target !== null)
    .map((variation) => normalizeUrlPath(new URL(variation.target!).pathname));

  const durationDays = convertMsToDays(interval);

  const { mainRangeResults, isLoading } = useAnalyticsReadForExperiments(
    id,
    startDatetime,
    adjustedEndDatetime,
    interval,
    variationTargetPagePaths,
    pageType,
    isFractional,
  );

  const totalSessions = mainRangeResults
    ? mainRangeResults.reduce((total, rangeResult) => {
        const sessions = rangeResult.metrics.unique_sessions?.[0] || 0;
        return total + sessions;
      }, 0)
    : 0;

  const experimentResults = variations
    .filter((variation) => variation.target !== null)
    .map((variation) => {
      const targetURL = new URL(variation.target!);
      const urlPath = normalizeUrlPath(targetURL.pathname);

      const variationSlug = variation.slug;

      const associatedRangeResult: RangeResult | null =
        mainRangeResults?.find(
          (rangeResult) => rangeResult.urlPath === urlPath,
        ) ?? null;

      const metrics = associatedRangeResult?.metrics ?? {};

      return {
        targetURL,
        variationSlug,
        metrics,
      };
    });

  const getWinnerDisplay = () => {
    if (!endedAt) {
      return null;
    }

    return winner?.slug ?? "No Winner";
  };

  if (!experiment || !workspaceId) {
    return null;
  }

  const experimentsListPath = Boolean(projectId)
    ? generatePath(routes.shops.experiments.list, {
        projectId,
      })
    : generatePath(routes.workspace.experiments.list, {
        workspaceId,
      });

  return (
    <ExperimentsLayout
      headerTitle={
        <div className="flex flex-row items-center gap-1">
          <ExperimentEditableHeader
            title={name}
            experimentId={id}
            dispatchExperimentEdit={dispatchExperimentEdit}
            handleReactHookFormSubmit={handleReactHookFormSubmit}
            isLoadingRequiredData={!workspaceId}
          />
          {experiment && (
            <Tag
              variant={
                experimentStatusMapping[getExperimentStatus(experiment)].variant
              }
              size="base"
            >
              {experimentStatusMapping[getExperimentStatus(experiment)].label}
            </Tag>
          )}
        </div>
      }
      headerActions={<ExperimentActionsBar experiment={experiment} />}
      onBackClick={() => {
        navigate(experimentsListPath);
      }}
    >
      <ExperimentTabWrapper>
        <div className="flex flex-col gap-4">
          <ExperimentResultsTabLinkInfo experiment={experiment} />
          <div className="grid grid-cols-12 gap-4">
            <ValuesContainer className="col-span-6">
              <TitleWithValue
                title="Sessions"
                value={formatWithCommasAndTwoDecimals(totalSessions, false)}
              />
              <TitleWithValue
                title="Duration"
                value={`${durationDays} day${durationDays !== 1 ? "s" : ""}`}
              />
              <TitleWithValue title="Winner" value={getWinnerDisplay()} />
            </ValuesContainer>

            <ValuesContainer className="col-span-6">
              <TitleWithValue title="Start Date" value={createdAtHuman} />
              <TitleWithValue title="End Date" value={endedAtHuman} />
            </ValuesContainer>
          </div>
          <div className="flex flex-row items-center justify-end">
            <ExperimentPageTypeSelect
              pageType={pageType}
              isFractional={isFractional}
              onChange={(newPageType, newIsFractional) => {
                setPageType(newPageType);
                setIsFractional(newIsFractional);
              }}
            />
          </div>
          {!isLoading ? (
            <div className="flex flex-col gap-2">
              <Table>
                <TableHeader>
                  <TableRow>
                    <CustomTableHead
                      label="Variant name"
                      className="w-[300px]"
                    />
                    {OVERVIEW_TABLE_METRICS.map((metric) => (
                      <CustomTableHead
                        key={metric.key}
                        label={metric.label}
                        tooltip={metric.tooltip}
                        className="w-[150px]"
                      />
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {experimentResults?.map((experimentResult, index) => {
                    return (
                      <ExperimentResultsTableRow
                        key={experimentResult.variationSlug}
                        experimentResult={experimentResult}
                        index={index}
                        isFractional={isFractional}
                        currency={currency}
                      />
                    );
                  })}
                </TableBody>
              </Table>
              <StatSigInfoText />
            </div>
          ) : (
            <Loader />
          )}
        </div>
      </ExperimentTabWrapper>
    </ExperimentsLayout>
  );
};

const ExperimentResultsTab: React.FC = () => {
  const { simpleExperiment, workspaceId, experiment } = useExperimentDetails();

  if (!experiment) {
    return null;
  }

  if (!simpleExperiment || !workspaceId) {
    return <Loader />;
  }
  return (
    <ExperimentResultsTabInner
      simpleExperiment={simpleExperiment}
      workspaceId={workspaceId}
      experiment={experiment}
    />
  );
};

type ExperimentResult = {
  targetURL: URL;
  variationSlug: string;
  metrics: RangeResult["metrics"];
};

type ExperimentResultsTableRowProps = {
  experimentResult: ExperimentResult;
  index: number;
  isFractional: boolean;
  currency: CurrencyCode;
};

const ExperimentResultsTableRow: React.FC<ExperimentResultsTableRowProps> = ({
  experimentResult,
  index,
  isFractional,
  currency,
}) => {
  const { targetURL, variationSlug, metrics } = experimentResult;

  return (
    <TableRow>
      <TableCell>
        <div className="grid grid-cols-[auto,1fr] gap-x-2 gap-y-1">
          <div className="flex flex-row items-center gap-2">
            <VariationNameStartEnhancer index={index} size="sm" />
            <Tooltip triggerAsChild content={targetURL.toString()} delay={800}>
              <span className="text font-medium">{variationSlug}</span>
            </Tooltip>
          </div>
          <div />
          <div className="flex items-center gap-1 truncate">
            <Tooltip triggerAsChild content={targetURL.toString()} delay={800}>
              <span className="text-muted truncate text-xs font-medium">
                {targetURL.toString()}
              </span>
            </Tooltip>
            <Link
              to={targetURL.toString()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      </TableCell>
      {OVERVIEW_TABLE_METRICS.map((metric) => {
        const currentMetricValues = metrics[metric.key] ?? [];
        const value = currentMetricValues[0] ?? null;

        return (
          <TableCell key={metric.key}>
            <MetricWithDelta
              name={metric.key}
              value={value}
              doesCompareValueExist={false}
              wrapperClassName="justify-start flex-grow"
              valueClassName="text-xs"
              touchType="all"
              isFractional={isFractional}
              currency={currency}
            />
          </TableCell>
        );
      })}
    </TableRow>
  );
};

type CustomTableHeadProps = {
  label: string;
  tooltip?: string;
  className?: string;
};

const CustomTableHead = ({
  label,
  tooltip,
  className,
}: CustomTableHeadProps) => {
  return (
    <TableHead className={className}>
      <div className="flex items-center gap-2">
        {tooltip ? (
          <Tooltip content={tooltip} side="top" triggerAsChild>
            <span
              className={twMerge(
                "truncate",
                "border-placeholder border-b-[1px] border-dashed",
              )}
            >
              {label}
            </span>
          </Tooltip>
        ) : (
          <span className="truncate">{label}</span>
        )}
      </div>
    </TableHead>
  );
};

export default ExperimentResultsTab;
